import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from '@app-core/services/data/data.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import {
  DRIVERS_LIST_COLUMNS,
  DriverStatus,
  DriverType,
  ManageAction,
  PAGE_OPTION,
  QR_CODE_IMAGE_HEIGHT,
  QR_CODE_IMAGE_WIDTH,
} from '@app-driver-management/common/driver-management.constants';
import {
  Driver,
  DriverDetails,
  DriverDetailsResponse,
  QrCodeData,
  RegisteredDriver,
  UnifiedDriverDetails,
} from '@app-driver-management/common/driver-management.model';
import { BehaviorSubject, EMPTY, of, Subject, throwError } from 'rxjs';
import { DriverImagesComponent } from '../driver-images/driver-images.component';
import { catchError, concatMap, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { AccessService } from '@app-core/services/access/access.service';
import { DriverManagementService } from '@app-driver-management/services/driver-management.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckbox } from '@angular/material/checkbox';
import jsPDF from 'jspdf';
import { CLIENT_CONFIG } from '@config/config';

@Component({
  selector: 'app-driver-list',
  templateUrl: './driver-list.component.html',
  styleUrls: ['./driver-list.component.scss'],
})
export class DriverListComponent implements OnChanges, OnDestroy, OnInit {
  @ViewChild('paginator', { static: true })
  public paginator: MatPaginator;
  @Input()
  public loader: boolean;
  @Input()
  public driverType: string;
  public pageSizeOptions: number[] = PAGE_OPTION;
  @Input()
  public driverList: DriverDetailsResponse;
  @Input()
  public fleetId: string;
  @Input()
  public currentDateFormat: string;
  @Input() public pageSize: number = 0;
  @Input()
  public currentSelectedFilterType: string = 'ALL';
  @Input()
  public totalCountOfDrivers: number = 0;
  public isClientSidePaginated: boolean = false;
  @Output()
  public doRefresh = new EventEmitter();
  @Output() pageChange = new EventEmitter<{ limit: number; offset: number }>();

  @Output() loadingStateChanged = new EventEmitter<boolean>();

  public selection = new SelectionModel<any>(true, []);
  public isFaceIdEnabled: boolean = false;
  public isFaceIdEnabled$ = new BehaviorSubject<boolean>(false);
  public csvLoader: boolean = false;
  public driverTypeEnum = DriverType;
  public manageDriverAction = ManageAction;
  public tableColumns = [];
  public tableSource: MatTableDataSource<any> = new MatTableDataSource([]);
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public DriverStatus = DriverStatus;
  public allSelected: boolean = false;
  @Output() toggleEvent = new EventEmitter<boolean>();
  @Output() selectedDriversCount = new EventEmitter<{ count: number; drivers }>();
  public selectedDriversForDeletion: Array<string>;
  public showLabel: boolean;
  @Input() DriverIdsForAssignment: string[];
  public hoveredDriver: UnifiedDriverDetails = null;
  public autoCompleteOpen = new Subject<Driver>();
  public checkselected: Array<Driver>;
  @ViewChild('myCheckbox') checkbox: MatCheckbox;
  public showDriverImageIcon = true; // Set this based on your feature flags or conditions
  public logo: string;
  public config = CLIENT_CONFIG;
  @Output()
  public selectedDriverIds = new EventEmitter();
  @Input() isQRCodeEnabled: boolean;
  @Output() qrDownloadSuccess = new EventEmitter<string>();

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private snackbarService: SnackBarService,
    private accessService: AccessService,
    private driverManagementService: DriverManagementService,
    private gtmService: GoogleTagManagerService,
    private translate: TranslateService,
    public dataService: DataService,
    public durationRangeService: DurationRangeService,
    private cdRef: ChangeDetectorRef,
    private mixpanelService: MixPanelService
  ) {}
  public ngOnInit() {
    const planDetails = this.durationRangeService.planDetails;
    const featureFlags = planDetails?.featureFlags || [];
    this.isFaceIdEnabled$.next(this.route.snapshot.data['faceIdEnabled']);
    this.isFaceIdEnabled$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isFaceIdEnabled) => {
      this.isFaceIdEnabled = isFaceIdEnabled;
      // Only pass isFaceIdEnabled if featureFlags contains 'face_recognition'
      const faceIdEnabledForColumns = !planDetails || featureFlags.includes('face_recognition') ? this.isFaceIdEnabled : undefined;
      this.showDriverImageIcon = faceIdEnabledForColumns;
      this.tableColumns = DRIVERS_LIST_COLUMNS();
    });
    if (planDetails) {
      if (!featureFlags?.includes('tagging_workflow')) {
        this.tableColumns = this.tableColumns.filter((col) => col !== 'tags');
      }
    }
    const {
      logo: { lightLogo },
    } = this.config;
    const logoUrl = lightLogo;
    this.convertImageToBase64(logoUrl).then((base64) => {
      this.logo = base64;
    });
  }
  public ngOnChanges(changes: SimpleChanges) {
    if (this.isClientSidePaginated) {
      this.changeToServerSidePaginator();
    }
    if (changes.loader && this.loader) {
      this.tableSource.data = new Array(10).fill(undefined);
    } else if (changes.driverList && this.driverList?.data?.length) {
      this.tableSource.data = this.driverList.data;
      const selectedDriverIds = this.checkselected?.map((item) => item.driverId);
      this.selection.clear();

      this.tableSource.data.forEach((row) => {
        if (selectedDriverIds?.includes(row.driverId)) {
          this.selection.select(row);
        }
      });
    } else {
      // Prevent premature reset
      if (!changes.driverList?.previousValue && !changes.driverList?.currentValue) {
        return;
      }
      this.tableSource.data = [];
    }

    if (!this.tableSource.paginator && this.paginator) {
      this.tableSource.paginator = this.paginator;
    }
    if (!this.isFaceIdEnabled && !this.isQRCodeEnabled) {
      this.tableColumns = this.tableColumns.filter((col) => col !== 'driverImages');
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onPageChange(event: PageEvent) {
    if (!this.isClientSidePaginated) {
      const limit = event.pageSize;
      const offset = event.pageIndex * event.pageSize;
      this.pageChange.emit({ limit, offset });
    }
  }

  closeAutoComplete(driver: any) {
    this.autoCompleteOpen.next(driver);
  }

  resetToFirstPage(): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  public changeToClientSidePagination(driverList: string[], totalCount: number) {
    if (driverList && totalCount) {
      // Start Client side pagination
      this.tableSource.paginator = this.paginator;
      this.isClientSidePaginated = true;
      // Stop server side pagination if searching
      this.ngUnsubscribe.next();
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.tableSource.data = driverList;
      this.totalCountOfDrivers = totalCount;
      return;
    }
  }

  public changeToServerSidePaginator() {
    // Server side pagination if there is not search string
    this.tableSource.paginator = undefined;
    this.isClientSidePaginated = false;
    // Unsubscribe previous subscriptions before subscribing again
    this.ngUnsubscribe.next();
  }

  public navigatToDriverDetails(route: string, driverId: string, driverName: string, event?) {
    event.stopPropagation();
    let queryParams = { driverId: driverId, driverType: this.driverType };
    if (driverName?.length > 0) {
      queryParams['driverName'] = driverName;
    }
    this.router.navigate([`/drivers/${route}`], {
      queryParams: queryParams,
    });
    this.mixpanelService.track('Navigating to Driver Details page', {
      'Driver ID': driverId,
      'Driver Type': this.driverType,
    });
  }

  public openDriverImagesDialog(driver: DriverDetails, infoMessage?: string, event?: Event, addDriverImages: boolean = false) {
    this.gtmService.viewDriverImages();
    event.stopPropagation();
    this.mixpanelService.track('View Driver Images In Driver List', {
      Driver: driver,
    });
    const dialogRef = this.dialog.open(DriverImagesComponent, {
      panelClass: ['driver-images-dialog', 'mobile-modal'],
      position: { top: '24px' },
      width: '510px',
      height: '596px',
      autoFocus: false,
      data: {
        driver,
        infoMessage,
        isQRCodeEnabled: this.isQRCodeEnabled,
        showDriverImageIcon: this.showDriverImageIcon,
        addDriverImages: addDriverImages,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.doRefresh.emit();
      }
    });
  }

  public viweDriverQRCode(driver: DriverDetails, event?: Event) {
    event.stopPropagation();
    this.dialog.open(DriverImagesComponent, {
      panelClass: ['driver-images-dialog', 'mobile-modal'],
      position: { top: '24px' },
      width: '510px',
      height: '596px',
      autoFocus: false,
      data: {
        driver,
        showDriverImageIcon: this.showDriverImageIcon,
        isQRCodeEnabled: this.isQRCodeEnabled,
      },
    });
  }

  public resendTempPasswordEmail(driver: DriverDetails) {
    driver.loader = true;
    const body = {
      email: driver.email,
    };
    this.gtmService.resendDriverTempPassword(this.fleetId);
    this.mixpanelService.track('Resend Temporary Password Email', {
      'Fleet Id': this.fleetId,
      Email: driver.email,
    });
    this.driverManagementService
      .resendDriverTempPassword(body)
      .pipe(
        finalize(() => {
          driver.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.snackbarService.success(this.translate.instant('driverManagementTempPasswordSuccess'));
        },
        () => {
          this.snackbarService.failure(this.translate.instant('driverManagementEmailFailed'));
        }
      );
  }
  public deleteUnifiedDriver(driver) {
    const isMultiple = Array.isArray(driver);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      position: { top: '24px' },
      minHeight: isMultiple ? '230px' : '164px',
      width: '332px',
      maxWidth: 'none',
      panelClass: ['delete-modal'],
      data: {
        title: this.translate.instant('Are you Sure?'),
        content: isMultiple
          ? this.translate.instant(
              'Are you sure you want to delete these drivers? This action will permanently remove all selected drivers and cannot be undone.'
            )
          : this.translate.instant('Do you really want to delete this driver? This action cannot be undone.'),
        actions: [
          { actionButton: this.translate.instant('CANCEL'), dialogCloseValue: false },
          { actionButton: this.translate.instant('DELETE'), dialogCloseValue: true, primary: true },
        ],
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap((confirmDelete: boolean) => {
          return confirmDelete ? this.deleteDriver(driver) : EMPTY;
        })
      )
      .subscribe(
        () => {
          this.snackbarService.success(this.translate.instant('driverManagementDriverDeleteSuccess'));
          this.doRefresh.emit();
        },
        () => {
          isMultiple
            ? this.snackbarService.failure(this.translate.instant('FailedToDeleteDriverInBulk'))
            : this.snackbarService.failure(this.translate.instant('driverManagementDriverDeleteFailed'));
        }
      );
  }
  public deleteDriver(driver: RegisteredDriver) {
    const driversArray = Array.isArray(driver) ? driver : [driver];
    driver.deleteDriverLoader = true;
    this.gtmService.deleteDriver(this.fleetId);
    this.mixpanelService.track('Deleting Driver', {
      'Fleet Id': this.fleetId,
      'Driver Id': driver.driverId,
    });

    const personIds = driversArray.map((drv) => drv?.appMetadata?.personId || drv.personId).filter((personId) => !!personId);

    if (personIds.length > 0) {
      return this.driverManagementService.deletePersonDetails({ personIds: personIds }).pipe(
        takeUntil(this.ngUnsubscribe),
        catchError((error) => {
          if (error.status === 404) {
            // If 404 error, proceed with deleting driver details
            return of(null);
          } else {
            // If other errors, rethrow the error
            return throwError(() => error);
          }
        }),
        concatMap(() => this.deleteDriverDetails(driver))
      );
    } else {
      return this.deleteDriverDetails(driver);
    }
  }

  private deleteDriverDetails(driver) {
    let requestPayload;

    if (Array.isArray(driver)) {
      requestPayload = driver.map(({ driverId, email, source }) => (email ? { driverId, email, source } : { driverId, source }));
    } else {
      const { driverId, email, source } = driver;
      requestPayload = email ? [{ driverId, email, source }] : [{ driverId, source }];
    }

    return this.driverManagementService.deleteUnifiedDriver(requestPayload).pipe(
      finalize(() => {
        driver.deleteDriverLoader = false;
        this.clearSelection();
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  public navigateToEditDriver(action: ManageAction, driver?: DriverDetails) {
    this.router.navigate(['./edit-driver'], {
      relativeTo: this.route,
      queryParams: {
        action,
        ...(driver && driver.driverId ? { driverType: this.driverType, driverId: driver.driverId } : {}),
      },
    });
    this.mixpanelService.track('Navigating to Edit Drivers Details Page', {
      Action: action,
      'Driver ID': driver.driverId || {},
      'Driver Type': this.driverType,
    });
  }

  public exportUsers() {
    this.csvLoader = true;
    const { customerName = '' } = this.accessService.getLoginInfo();
    const params = {
      userType: 'DRIVER',
      customerName,
      dateFormat: this.currentDateFormat,
    };

    this.gtmService.exportDriversCSV(this.fleetId);
    this.dataService
      .exportUsers(params)
      .pipe(
        finalize(() => (this.csvLoader = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: HttpResponse<Blob>) => {
          const defaultFilename = `${this.fleetId}_drivers.csv`;
          const contentDispositionHeader = res.headers.get('content-disposition');
          let filename = defaultFilename;
          if (contentDispositionHeader) {
            filename = this.dataService.extractFilename(contentDispositionHeader);
          }
          this.dataService.downloadFile(res.body, filename, res.headers.get('content-type'));
          this.snackbarService.success(this.translate.instant('driverManagementDriverListSuccess'));
        },
        () => {
          this.snackbarService.failure(this.translate.instant('driverManagementDriverListFailure'));
        }
      );
  }
  public openMenu(event: Event): void {
    event.stopPropagation();
  }
  public handleClick(event: Event, driver) {
    this.openMenu(event);
    this.resendTempPasswordEmail(driver);
  }

  public toggleAllRows() {
    if (this.allSelected) {
      this.selection.clear(); // Deselect all
      this.toggleEvent.emit(false);
      this.updateSelectionState();
      this.cdRef.detectChanges();
      return;
    } else if (!this.allSelected && this.selection.selected.length > 0) {
      this.checkbox.checked = false;
      this.allSelected = false;
      this.toggleEvent.emit(false);
      this.selection.clear();
      this.cdRef.detectChanges();
    } else {
      this.tableSource.data.forEach((row) => this.selection.select(row)); // Select all
      this.toggleEvent.emit(true);
    }
    if (this.selection.selected.length === 0) {
      this.selection.clear();
      this.updateSelectionState();
      this.clearSelection();
      this.cdRef.detectChanges();
    }
    this.updateSelectionState();
    this.cdRef.detectChanges();
  }

  public toggleRow(driver: UnifiedDriverDetails) {
    if (this.checkselected?.length) {
      const currentIds = this.selection.selected.map((d) => d.driverId);
      this.checkselected.forEach((item) => {
        if (!currentIds.includes(item.driverId)) {
          this.selection.select(item);
        }
      });
    }
    this.selection.toggle(driver);
    this.toggleEvent.emit(this.selection.selected.length > 0);
    this.selectedDriversForDeletion = this.selection.selected;
    this.selectedDriversCount.emit({ count: this.selection.selected.length, drivers: this.selection.selected });
    this.selectedDriverIds.emit(this.selection.selected.map((d) => d.driverId));
    this.updateSelectionState();
    this.cdRef.detectChanges();
  }

  public updateSelectionState() {
    this.allSelected = this.selection.selected.length > 0 && this.selection.selected.length === this.tableSource.data.length;
    this.selectedDriversForDeletion = this.selection.selected;
    this.checkselected = this.selection.selected;
    this.selectedDriversCount.emit({ count: this.selection.selected.length, drivers: this.selection.selected });
    this.selectedDriverIds.emit(this.selection.selected.map((d) => d.driverId));
    this.cdRef.detectChanges();
  }

  public clearSelection() {
    this.selection.clear();
    this.toggleEvent.emit(false);
    this.updateSelectionState();
    this.cdRef.detectChanges(); // Force UI update
  }

  public DeleteSelectedDrivers() {
    this.deleteUnifiedDriver(this.selectedDriversForDeletion);
  }
  public preventNavigate(event: Event): void {
    event.stopPropagation();
  }

  public downloadQRcode(driverId: string, driverName: string) {
    this.driverManagementService
      .getGenerateQRCode(driverId)
      .pipe(
        finalize(() => {}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((res: QrCodeData) => {
        const { data } = res;
        let QRCodeBase64 = String(data.qrCode);

        // Ensure it's a valid base64 image
        const base64Image = QRCodeBase64.startsWith('data:image') ? QRCodeBase64 : `data:image/png;base64,${QRCodeBase64}`;

        // Create a PDF and add the QR code image
        const doc = new jsPDF();

        // Add image to PDF (x, y, width, height)
        const imgWidth = QR_CODE_IMAGE_WIDTH;
        const imgHeight = QR_CODE_IMAGE_HEIGHT;
        const x = (210 - imgWidth) / 2; // Center horizontally (A4 width = 210mm)

        doc.addImage(base64Image, 'PNG', x, 20, imgWidth, imgHeight);

        // Add driver info below the QR code
        const driverIdLabel = this.translate.instant('DRIVERID');
        const driverNameLabel = this.translate.instant('DRIVERNAME');
        doc.setFontSize(14);
        doc.text(`${driverIdLabel}:`, 10, 230, { align: 'left' });
        doc.setFont('helvetica', 'bold'); // Set to bold
        doc.text(`${driverId || '-'}`, 10, 240, { align: 'left' });

        doc.setFont('helvetica', 'normal'); // Set back to normal
        doc.text(`${driverNameLabel}:`, 10, 250, { align: 'left' });
        doc.setFont('helvetica', 'bold'); // Set to bold
        doc.text(`${driverName || '-'}`, 10, 260, { align: 'left' });
        const base64logo = this.logo.startsWith('data:image') ? this.logo : `data:image/png;base64,${this.logo}`;
        doc.addImage(base64logo, 'PNG', 150, 260, 40, 20);
        // Save the PDF
        doc.save(`driver-${driverId}-QRCode.pdf`);
        const qrDownloadSuccessMsg = this.translate.instant('QR_CODE_DOWNLOADED_SUCCESSFULLY');
        this.qrDownloadSuccess.emit(qrDownloadSuccessMsg);
      });
  }

  private convertImageToBase64(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Important if the image is from another domain
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png'); // Or 'image/jpeg'
        resolve(dataURL);
      };
      img.onerror = (err) => {
        reject(err);
      };
      img.src = imageUrl;
    });
  }

  public getInitials(name: string): string {
    if (!name) return '';
    const words = name.trim().split(/\s+/);
    const first = words[0]?.[0] || '';
    const second = words[1]?.[0] || '';
    return (first + second).toUpperCase();
  }
}
